<template>
    <b-overlay :show="busySaving">
        <b-card style="min-height: 400px">
            <div v-if="selected_product_details != null">
                <!-- header -->
                <div class="d-flex">
                    <b-button variant="primary" size="sm" pill class="mb-2 mt-1 mr-2" @click="setSelectedProduct(-1)">
                        <i class="mdi mdi-arrow-left mr-1"></i> Back
                    </b-button>
                    <h4 class="mt-2 mb-2">
                        {{ selected_product_details.name }}
                    </h4>
                    <b-button variant="info" size="sm" pill class="ml-auto mb-2 mt-1 mr-2" @click="$emit('editProduct', selected_product_details.id)">
                        <i class="mdi mdi-square-edit-outline mr-1"></i> Edit Details
                    </b-button>
                </div>
                <!-- content -->
                <b-row>
                    <!-- left column (details) -->
                    <b-col cols="8">
                        <div class="mt-3">
                            <h6 class="text-success text-uppercase">
                                {{ selected_product_details.group_names }}
                            </h6>
                            <h5 class="mb-4">
                                <b>R{{ selected_product_details.price }}</b>
                            </h5>
                            <hr />
                            <div>
                                <h5>
                                    <i class="bx bxs-purchase-tag-alt font-size-16 me-1"></i>
                                    Properties
                                </h5>
                                <!-- tiles of all the properties assigned to the product -->
                                <div class="propertyContainer">
                                    <propertyTile
                                        v-for="item in selected_product_details.properties"
                                        :property="item"
                                        :key="item.id"
                                        @editProperty="handleEditProperty"
                                        class="propertyTile border border-light"
                                    >
                                    </propertyTile>
                                </div>
                            </div>

                            <!-- Description -->
                            <div v-if="selected_product_details.description" class="mt-4">
                                <h5>
                                    <i class="bx bxs-info-circle font-size-16 me-1"></i>
                                    Description
                                </h5>
                                <div class="product-description" v-html="selected_product_details.description"></div>
                            </div>
                        </div>
                    </b-col>

                    <!-- right column (images) -->
                    <b-col cols="4" v-if="selected_product_details">
                        <!-- Image Carousel -->
                        <div v-if="productImages.length > 0">
                            <b-carousel
                                id="product-image-carousel"
                                :key="productImages.length"
                                controls
                                indicators
                                :interval="0"
                                style="background: #f8f9fa; border-radius: 8px;"
                            >
                                <b-carousel-slide
                                    v-for="image in productImages"
                                    :key="image.id"
                                >
                                    <template #img>
                                        <img
                                            :src="image.public_url"
                                            :alt="image.original_filename || 'Product image'"
                                            style="height: 400px; width: 100%; object-fit: contain;"
                                            class="d-block"
                                        />
                                    </template>
                                </b-carousel-slide>
                            </b-carousel>

                            <!-- Image Thumbnails / Management -->
                            <div class="d-flex flex-wrap mt-2" style="gap: 8px;">
                                <div
                                    v-for="image in productImages"
                                    :key="'thumb-' + image.id"
                                    class="image-thumb-wrapper"
                                >
                                    <img
                                        :src="image.public_url"
                                        class="image-thumb"
                                        :class="{ 'border-primary': image.is_primary }"
                                    />
                                    <b-badge
                                        v-if="image.is_primary"
                                        variant="primary"
                                        class="primary-badge"
                                    >Primary</b-badge>
                                    <b-button
                                        size="sm"
                                        variant="danger"
                                        class="delete-btn"
                                        @click="handleDeleteImage(image.id)"
                                    >&times;</b-button>
                                    <b-button
                                        v-if="!image.is_primary"
                                        size="sm"
                                        variant="outline-primary"
                                        class="mt-1 p-0 w-100"
                                        style="font-size: 9px;"
                                        @click="handleSetPrimary(image.id)"
                                    >Set Primary</b-button>
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-center py-5 text-muted">
                            <i class="mdi mdi-image-outline" style="font-size: 48px;"></i>
                            <p>No images uploaded</p>
                        </div>

                        <!-- Upload -->
                        <uploader
                            :options="renderUploadOptions"
                            :autoStart="true"
                            :ref="'attach_upload'"
                            class="uploader-example mt-3"
                            @file-added="onFileAdded"
                            @file-success="onFileSuccess"
                        >
                            <uploader-unsupport></uploader-unsupport>
                            <uploader-drop>
                                <p>Drop files here to upload or</p>
                                <uploader-btn class="btn">Select Files</uploader-btn>
                            </uploader-drop>
                            <uploader-list></uploader-list>
                        </uploader>
                    </b-col>
                </b-row>
            </div>

            <propertyModal v-if="show_modal" :property_id="edit_property_id" :product_id="selected_product_id" />

            <b-overlay :show="selected_product_details == null" no-wrap></b-overlay>
        </b-card>
    </b-overlay>
</template>

<script>
import { productComputed, productMethods } from "@/state/helpers";
import propertyTile from "./propertyTile.vue";
import propertyModal from "../properties/propertyModal.vue";

export default {
    components: {
        propertyTile,
        propertyModal,
    },
    data: () => ({
        busySaving: false,
        hasMounted: false,
        show_modal: false,
        edit_property_id: -1,
    }),
    computed: {
        ...productComputed,
        productImages() {
            return this.product_images || [];
        },
        renderUploadOptions() {
            var storage = window.localStorage;
            var AUTH_TOKEN = storage.getItem("x_token");
            var url = process.env.VUE_APP_API_BASEURL + "api/products/uploadimage";
            var options = {
                autoStart: true,
                target: url,
                query: {
                    product_id: this.selected_product_id,
                },
                headers: {
                    Authorization: "Bearer " + AUTH_TOKEN,
                },

                testChunks: false,
                withCredentials: false,
                fileParameterName: "image",
                singleFile: false,
            };

            return options;
        },
    },
    methods: {
        ...productMethods,
        //eslint-disable-next-line
        onFileAdded() {
            this.busySaving = true;
        },
        //eslint-disable-next-line
        onFileSuccess(e) {
            this.getProductImages(this.selected_product_id).then(() => {
                this.getProductDetails().then(() => {
                    this.busySaving = false;
                    // Reset the uploader file list
                    if (this.$refs.attach_upload) {
                        this.$refs.attach_upload.uploader.cancel();
                    }
                });
            });
        },
        isProductLoaded() {
            if (this.selected_product_details == null) {
                return false;
            }
            return this.selected_product_details.id == this.selected_product_id;
        },
        handleEditProperty(property_id) {
            this.edit_property_id = property_id;
        },
        handleDeleteImage(imageId) {
            this.$swal
                .fire({
                    title: "Delete this image?",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#f46a6a",
                    confirmButtonText: "Delete",
                })
                .then((result) => {
                    if (result.value) {
                        this.busySaving = true;
                        this.deleteProductImage(imageId).then(() => {
                            this.getProductImages(this.selected_product_id).then(() => {
                                this.getProductDetails().then(() => {
                                    this.busySaving = false;
                                });
                            });
                        });
                    }
                });
        },
        handleSetPrimary(imageId) {
            this.busySaving = true;
            this.setPrimaryProductImage(imageId).then(() => {
                this.getProductImages(this.selected_product_id).then(() => {
                    this.getProductDetails().then(() => {
                        this.busySaving = false;
                    });
                });
            });
        },
        showPropertyModal() {},
    },

    mounted() {
        if (this.isProductLoaded()) {
            this.hasMounted = true;
            this.getProductImages(this.selected_product_id);
        } else {
            this.getProductDetails().then(() => {
                this.hasMounted = true;
                this.getProductImages(this.selected_product_id);
            });
        }

        //* load properties if the 'all_properties' array is empty
        this.getProperties();
    },
};
</script>

<style scoped>
.propertyContainer {
    display: flex;
    flex-flow: row wrap;
}
.propertyTile {
    margin: 0.25rem 0.25rem 0.35rem 0.25rem;
    height: 2.5rem;
    width: 12rem;
    padding: 0.5rem;
    position: relative;
}
.product-description {
    line-height: 1.6;
    word-wrap: break-word;
}
.product-description >>> img {
    max-width: 100%;
    height: auto;
}
.image-thumb-wrapper {
    position: relative;
    width: 60px;
}
.image-thumb {
    width: 60px;
    height: 60px;
    object-fit: cover;
    border-radius: 4px;
    border: 2px solid transparent;
}
.primary-badge {
    position: absolute;
    top: -4px;
    left: -4px;
    font-size: 9px;
}
.delete-btn {
    position: absolute;
    top: -4px;
    right: -4px;
    width: 18px;
    height: 18px;
    font-size: 10px;
    line-height: 1;
    padding: 0;
}
</style>
